<template>
  <div :class="containerClass">
    <page-header
      title="AI Provider Configuration"
      :container-class="null"
      :show-back="false"
      class="mb-32pt"
    />

    <!-- Active grader widget (§1.3.3) -->
    <div class="active-grader mb-3">
      <div class="active-grader__icon">
        <i class="material-icons">memory</i>
      </div>
      <div class="active-grader__body">
        <div class="active-grader__label">Currently active grader</div>
        <div v-if="active" class="active-grader__value">
          <strong>{{ active.provider_display || active.provider }}</strong>
          <span class="text-muted ml-2 small">{{ active.default_model || '—' }}</span>
        </div>
        <div v-else class="active-grader__value text-danger">
          <strong>No provider active</strong>
          <span class="text-muted ml-2 small">AI-graded submissions will fail until one is activated</span>
        </div>
      </div>
      <b-btn size="sm" variant="outline-secondary" @click="refresh" :disabled="isLoading">
        <i class="material-icons icon-16pt mr-1">refresh</i>
        Refresh
      </b-btn>
    </div>

    <p class="page-hint mb-3">
      Exactly one provider may be active at a time. Rows are seeded by the
      backend &mdash; activating one provider deactivates the other automatically.
    </p>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner class="align-middle mr-2"></b-spinner>
      <strong>Loading providers...</strong>
    </div>

    <div v-else-if="!providers.length" class="text-center py-5">
      <p class="text-muted">No AI provider rows found.</p>
      <b-btn variant="primary" @click="refresh">Retry</b-btn>
    </div>

    <div v-else class="row">
      <div v-for="(p, idx) in providers" :key="p.id" class="col-12 col-lg-6 mb-4">
        <provider-card
          :provider="p"
          :is-saving="savingId === p.id"
          :is-activating="activatingId === p.id"
          :server-errors="serverErrorsById[p.id] || {}"
          @save="onSave($event, idx)"
          @activate="onActivate"
          @deactivate="onDeactivate"
          @no-op="makeToast({ variant: 'info', msg: 'No changes to save.' })"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '@/components/Page.vue';
import ProviderCard from './components/ProviderCard.vue';

export default {
  name: 'AiProviderConfig',
  components: { PageHeader, ProviderCard },
  extends: Page,
  data() {
    return {
      title: 'AI Provider Configuration',
      isLoading: true,
      providers: [],
      active: null,
      savingId: null,
      activatingId: null,
      serverErrorsById: {},
    };
  },
  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'AI Provider Config', active: true },
      ];
    },
  },
  methods: {
    ...mapActions('aiProviderConfig', [
      'getAiProviderConfigs',
      'getActiveAiProviderConfig',
      'updateAiProviderConfig',
      'activateAiProviderConfig',
      'deactivateAiProviderConfig',
    ]),

    unwrapList(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.results)) return payload.results;
      if (payload.data) {
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.data.results)) return payload.data.results;
      }
      return [];
    },

    extractServerErrors(err) {
      // DRF returns 400 with { field: ["msg"], ... } or { non_field_errors: [...] }.
      // 403 returns { detail: "..." } — surface as a toast, not field-level.
      if (err && err.response && err.response.status === 400 && err.response.data) {
        return err.response.data;
      }
      return null;
    },

    extractToastMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      if (!data) return fallback;
      if (typeof data === 'string') return data;
      if (data.detail) return data.detail;
      if (data.message) return data.message;
      if (Array.isArray(data.non_field_errors)) return data.non_field_errors[0];
      // Best-effort: first field's first message.
      const firstKey = Object.keys(data)[0];
      const v = firstKey ? data[firstKey] : null;
      if (Array.isArray(v)) return `${firstKey}: ${v[0]}`;
      if (typeof v === 'string') return `${firstKey}: ${v}`;
      return fallback;
    },

    clearErrors(id) {
      if (this.serverErrorsById[id]) this.$set(this.serverErrorsById, id, {});
    },

    async refresh() {
      this.isLoading = true;
      try {
        const [listRes, activeRes] = await Promise.all([
          this.getAiProviderConfigs(),
          this.getActiveAiProviderConfig().catch(() => null),
        ]);
        this.providers = this.unwrapList(listRes);
        this.active = activeRes || this.providers.find(p => p.is_active) || null;
      } catch (e) {
        this.makeToast({
          variant: 'danger',
          msg: this.extractToastMessage(e, 'Failed to load AI provider configs'),
        });
      } finally {
        this.isLoading = false;
      }
    },

    async onSave({ id, payload }) {
      this.savingId = id;
      this.clearErrors(id);
      try {
        await this.updateAiProviderConfig({ id, data: payload });
        this.makeToast({ variant: 'success', msg: 'Provider updated' });
        await this.refresh();
      } catch (e) {
        const fieldErrors = this.extractServerErrors(e);
        if (fieldErrors) this.$set(this.serverErrorsById, id, fieldErrors);
        this.makeToast({
          variant: 'danger',
          msg: this.extractToastMessage(e, 'Failed to save provider'),
        });
      } finally {
        this.savingId = null;
      }
    },

    async onActivate({ id, providerLabel }) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Switch active AI grader to "${providerLabel}"? All future submissions will be graded by this provider.`,
        {
          title: 'Switch Active Provider',
          okVariant: 'success',
          okTitle: 'Switch & Activate',
          cancelTitle: 'Cancel',
          centered: true,
        },
      );
      if (!confirmed) return;

      this.activatingId = id;
      this.clearErrors(id);
      try {
        await this.activateAiProviderConfig(id);
        this.makeToast({ variant: 'success', msg: `${providerLabel} is now the active grader.` });
        await this.refresh();
      } catch (e) {
        const fieldErrors = this.extractServerErrors(e);
        if (fieldErrors) this.$set(this.serverErrorsById, id, fieldErrors);
        this.makeToast({
          variant: 'danger',
          msg: this.extractToastMessage(e, 'Failed to activate provider'),
        });
      } finally {
        this.activatingId = null;
      }
    },

    async onDeactivate({ id, providerLabel }) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Deactivate "${providerLabel}"? After this no provider will be active and AI-graded submissions will fail until another provider is activated.`,
        {
          title: 'Deactivate Provider',
          okVariant: 'warning',
          okTitle: 'Deactivate',
          cancelTitle: 'Cancel',
          centered: true,
        },
      );
      if (!confirmed) return;

      this.activatingId = id;
      this.clearErrors(id);
      try {
        await this.deactivateAiProviderConfig(id);
        this.makeToast({ variant: 'warning', msg: `${providerLabel} deactivated. No provider is currently active.` });
        await this.refresh();
      } catch (e) {
        this.makeToast({
          variant: 'danger',
          msg: this.extractToastMessage(e, 'Failed to deactivate provider'),
        });
      } finally {
        this.activatingId = null;
      }
    },
  },
  created() {
    this.refresh();
  },
};
</script>

<style scoped>
.active-grader {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 1rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.active-grader__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.active-grader__icon i { color: #4f46e5; font-size: 20px; }
.active-grader__body { flex: 1 1 auto; }
.active-grader__label {
  font-size: 0.72rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.1rem;
}
.active-grader__value { font-size: 0.95rem; }
.page-hint {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}
</style>
